import React, { useContext, useMemo } from "react";
import { useParams } from "react-router";
import { NavLink } from "react-router-dom";
import { useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ClavaContext } from "../../../config/contexts";
import DateSelector from "../../components/DateSelector";
import MatchesCard from "../../components/MatchesCard";
import Leagues from "../../components/League";
import { isFiniteDate, toLocalMidnight } from "../../../config/utils";
import { AdSlot } from "../../components/AdSlot";
import { AdPositionEnum } from "../../../client/api";
import { pathTeamManagement } from "../../../config/routes";
import { translate } from "../../../config/translator";
import { selectLanguageOrDefault } from "../../../selectors";
import { insiderIcon } from "../../icons/FAIcon";
import NewsPanel from "../../components/NewsPanel";
import { useNews } from "../../../hooks/useNews";

const TeamManagementCard: React.FC<{ l: any }> = ({ l }) => (
    <div className="w-full flex flex-col gap-2 py-2 px-3 xl:py-2 xl:px-3 rounded-xl bg-bg-secondary dark:bg-bg-secondary-dark border border-light-gray dark:border-light-gray-dark">
        <div className="text-base font-medium text-font dark:text-font-dark flex items-center justify-between">
            <span>{translate("teamManagement", l)}</span>
            <FontAwesomeIcon
                {...insiderIcon}
                className="text-insider dark:text-insider-dark text-3xl leading-none"
            />
        </div>
        <p className="text-font-600 dark:text-font-600-dark text-sm leading-relaxed text-left mb-2">
            {translate("teamManagementPromo", l)}
        </p>
        <div className="w-full flex justify-end">
            <NavLink
                to={pathTeamManagement()}
                className="rounded-full py-1 px-4 flex flex-row items-center justify-center border-0 bg-insider dark:bg-insider-dark hover:opacity-90 active:opacity-95 text-font-dark text-sm"
            >
                {translate("accediOra", l)}
            </NavLink>
        </div>
    </div>
);

const HomePage: React.FC = () => {
    const params = useParams();
    const l = useSelector(selectLanguageOrDefault);
    const { user } = useContext(ClavaContext);

    const selectedDate = useMemo(() => {
        const ymd = params.date;
        if (!ymd) return toLocalMidnight(new Date());
        const [y, m, d] = ymd.split("-").map((n) => parseInt(n, 10));
        const dt = new Date(y, m - 1, d);
        return isFiniteDate(dt) ? toLocalMidnight(dt) : toLocalMidnight(new Date());
    }, [params.date]);

    const areaOfInterestId = user?.areaOfInterest?.id;
    const { news, loading: newsLoading } = useNews({
        areaOfInterestId,
        limit: 7,
        offset: 0,
    });

    return (
        <div className="HomePage w-full px-6 xl:px-8 mb-8">
            {/* Compact Layout (<1280px) */}
            <div className="block xl:hidden">
                <div className="tablet-rail mx-auto w-full px-0 box-border flex flex-col gap-6">
                    <div className="panel w-full min-w-0 box-border">
                        <Leagues
                            showFlow
                            showInfo={false}
                            showLegal={false}
                            noOuterPadding
                            className="pt-0"
                        />
                    </div>

                    {user?.groups && user.groups.length > 0 && (
                        <div className="panel w-full min-w-0 box-border">
                            <TeamManagementCard l={l} />
                        </div>
                    )}

                    <div className="panel w-full min-w-0 box-border flex justify-center">
                        <div className="w-full max-w-md bg-white dark:bg-bg-secondary-dark overflow-hidden py-4 px-4 rounded-xl">
                        <a
                            href="https://hello.clava-sports.com/ordinanza-pqm/"
                            target="_blank"
                            rel="noreferrer"
                        >
                            <img
                                src="https://hello.clava-sports.com/wp-content/uploads/2025/12/Ordinanza-PQM.png"
                                className="w-full"
                                alt="Dispositivo dell’Ordinanza dd. 02.12.2025 - Trib. VE, RG 13538/2025."
                                title="Dispositivo dell’Ordinanza dd. 02.12.2025 - Trib. VE, RG 13538/2025."
                            />
                        </a>
                        </div>
                    </div>

                    {news.length > 0 || newsLoading ? (
                        <div className="panel w-full min-w-0 box-border">
                            <NewsPanel news={news} loading={newsLoading} />
                        </div>
                    ) : null}

                    <div className="panel w-full min-w-0 box-border flex flex-col gap-4">
                        <div className="w-full min-w-0">
                            <DateSelector />
                        </div>
                        <div className="w-full min-w-0">
                            <MatchesCard selectedDate={selectedDate} />
                        </div>
                        <div className="w-full min-w-0">
                            <AdSlot
                                type={AdPositionEnum.HOME_SCREEN_BANNER}
                                className="w-full"
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Desktop (≥1280px) */}
            <div className="hidden xl:block">
                <div className="mx-auto px-4 max-w-6xl xl:max-w-7xl">
                    <div className="grid items-start xl:grid-cols-12 gap-6 -mt-4 xl:pt-2">
                        {/* LEFT Side */}
                        <aside className="xl:col-span-3 self-start min-w-0 flex flex-col gap-4">
                            <Leagues
                                showFlow
                                showInfo={false}
                                showLegal={false}
                                noOuterPadding
                                className="pt-0 -mt-3"
                            />
                        </aside>

                        {/* CENTER*/}
                        <main className="xl:col-span-6 self-start min-w-0">
                            <div className="flex flex-col gap-4 w-full min-w-0 min-h-96 max-w-none">
                                <div className="w-full min-w-0">
                                    <DateSelector />
                                </div>
                                <div className="w-full min-w-0">
                                    <MatchesCard selectedDate={selectedDate} />
                                </div>
                                <div className="w-full min-w-0">
                                    <AdSlot
                                        type={AdPositionEnum.HOME_SCREEN_BANNER}
                                        className="w-full"
                                    />
                                </div>
                            </div>
                        </main>

                        {/* RIGHT */}
                        <aside className="xl:col-span-3 self-start min-w-0 w-full">
                            {user?.groups && user.groups.length > 0 ? (
                                <div className="hidden xl:block [@media(pointer:coarse)]:hidden w-full">
                                    <TeamManagementCard l={l} />
                                </div>
                            ) : null}
                            <div
                                className={`hidden xl:block [@media(pointer:coarse)]:hidden w-full ${user?.groups && user.groups.length > 0 ? "mt-4" : ""} bg-white dark:bg-bg-secondary-dark overflow-hidden py-5 px-6 xl:py-4 xl:px-8 mb-4 relative rounded-xl`}
                            >
                                <a
                                    href="https://hello.clava-sports.com/ordinanza-pqm/"
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    <img
                                        src="https://hello.clava-sports.com/wp-content/uploads/2025/12/Ordinanza-PQM.png"
                                        className="w-full"
                                        alt="Dispositivo dell’Ordinanza dd. 02.12.2025 - Trib. VE, RG 13538/2025."
                                        title="Dispositivo dell’Ordinanza dd. 02.12.2025 - Trib. VE, RG 13538/2025."
                                    />
                                </a>
                            </div>

                            {(news.length > 0 || newsLoading) && (
                                <div className={`hidden xl:block [@media(pointer:coarse)]:hidden w-full ${user?.groups && user.groups.length > 0 ? "mt-4" : ""}`}>
                                    <NewsPanel news={news} loading={newsLoading} />
                                </div>
                            )}
                        </aside>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default HomePage;
